import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { AssetClass, Symbol, Algo, Broker } from "../types";
import AddAlgoModal from "../components/Modals/AddAlgoModal";
import { updateLinkedAlgo } from "../services/api";
import AlgoEditModal from "../components/Modals/AlgoEditModal";
import axios from "axios";
import { getCSRFToken } from "../utils/csrf";

const getAccountLabel = (broker: any, account: any) => {
  const accountId = String(account.account_id || "");
  const normalizedId = accountId.replace("D#", "");
  
  const accountNames: Record<string, string> = {
    "2109573": "marykate",
    "2304256": "2-9/21",
    "2304257": "3-gold-1k-500",
    "2304258": "4-gold-1k-500",
    "2304259": "5-gold-1k-500",
    "2304262": "6-nq-1k-500",
    "2304263": "7-nq-1k-500",
    "2304264": "8-nq-1k-500",
    "2304265": "9-nq-10k-500",
    "2304266": "10-nq-10k-500"
  };

  const nick = accountNames[normalizedId];
  if (nick) {
    return `${nick} (${account.account_id})`;
  }

  const email = broker.login || "";
  let emailNick = email.split("@")[0] || broker.name || "Account";
  if (emailNick.includes("marykate")) {
    emailNick = "marykate";
  }
  return `${emailNick} - #${account.account_number || account.id} (${account.account_id})`;
};

export interface LinkedAlgo {
  id: number;
  name: string;
  description: string;
  parameters: any;
  parent_algo_id: number;
  instance_id: number;
  created_at: string;
  asset_classes: AssetClass[];
  symbols: Symbol[];
  cash_at_risk: number;
  returns: number;
  timeframe: string;
}

export interface TradingInstance {
  id: number;
  name: string;
  status: string;
  balance: number;
  profit: number;
  linked_algos: LinkedAlgo[];
  asset_classes: AssetClass[];
  symbols: Symbol[];
  trades: any[];
  notifications: any[];
  total_cash_at_risk?: number;
  cash_at_risk?: number;
  broker_account_id: number;
  broker_account: any;
  initial_balance: number;
  created_at: string;
  updated_at: string;
  telegram_enabled: boolean;
  telegram_bot_token?: string;
  telegram_chat_id?: string;
  telegram_notifications?: {
    send_entries: boolean;
    send_updates: boolean;
    send_exits: boolean;
    send_system_notifications: boolean;
    send_hourly_status: boolean;
    include_emoji: boolean;
    include_trade_details: boolean;
  };
}

const ManageInstance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [instance, setInstance] = useState<TradingInstance | null>(null);
  const [instanceName, setInstanceName] = useState("");
  const [selectedAlgo, setSelectedAlgo] = useState("");
  const [editingAlgo, setEditingAlgo] = useState<LinkedAlgo | null>(null);
  const [showAlgoEditModal, setShowAlgoEditModal] = useState(false);
  const [algos, setAlgos] = useState<Algo[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Telegram notification state
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [telegramNotifications, setTelegramNotifications] = useState({
    send_entries: true,
    send_updates: true,
    send_exits: true,
    send_system_notifications: true,
    send_hourly_status: true,
    include_emoji: true,
    include_trade_details: true,
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchInstanceDetails = async (isUpdate = false) => {
    try {
      if (!isUpdate) {
        setLoading(true);
      } else {
        setUpdating(true);
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}`
      );

      // Only update the instance if we have valid data
      if (response.data) {
        setInstance(response.data);
        if (!isUpdate) {
          setInstanceName(response.data.name);
          setSelectedAccount(response.data.broker_account_id?.toString() || "");
          setTelegramEnabled(response.data.telegram_enabled || false);
          setTelegramBotToken(response.data.telegram_bot_token || "");
          setTelegramChatId(response.data.telegram_chat_id || "");
          setTelegramNotifications(
            response.data.telegram_notifications || {
              send_entries: true,
              send_updates: true,
              send_exits: true,
              send_system_notifications: true,
              send_hourly_status: true,
              include_emoji: true,
              include_trade_details: true,
            }
          );
        }
      }
    } catch (error) {
      console.error("Failed to fetch instance details:", error);
      if (!isUpdate) {
        setError("Failed to load instance details");
      }
    } finally {
      if (!isUpdate) {
        setLoading(false);
      } else {
        setUpdating(false);
      }
    }
  };

  const fetchAllData = async () => {
    try {
      const [algosRes, brokersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/algos`),
        axios.get(`${BACKEND_URL}/api/brokers`),
      ]);
      setAlgos(algosRes.data);
      setBrokers(brokersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError("Failed to load required data");
    }
  };

  useEffect(() => {
    fetchInstanceDetails();
    fetchAllData();
  }, [id]);

  // Handle navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (hasUnsavedChanges && !updating) {
          saveAllChanges();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasUnsavedChanges, updating]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstanceName(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleAccountChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccount(e.target.value);
    setHasUnsavedChanges(true);
  };




  const handleAddAlgo = async (algo: Algo) => {
    if (!instance) return;

    if (!algo) {
      setError("Algo not found");
      return;
    }

    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      const uniqueId = Date.now().toString().slice(-4);
      const uniqueName = `${algo.name}_${instance.name}_${uniqueId}`;

      // Create the new linked algo data
      const newLinkedAlgo = {
        name: uniqueName,
        description: algo.description,
        parameters: algo.parameters,
        parent_algo_id: algo.id,
        asset_classes: [],
        symbols: [],
      };

      // Update the instance with the new linked algo
      const response = await axios.put(
        `${BACKEND_URL}/api/trading-instances/${instance.id}`,
        {
          linked_algos: [...instance.linked_algos, newLinkedAlgo],
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      console.log("Response data:", response.data);
      // Update the instance state with the response data
      setInstance(response.data);
      setSelectedAlgo("");
    } catch (error) {
      console.error("Failed to add algo:", error);
      setError("Failed to add algorithm");
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveAlgo = async (algoId: number) => {
    if (!instance) return;

    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      await axios.delete(
        `${BACKEND_URL}/api/instances/${instance.id}/linked-algos/${algoId}`,
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );
      setInstance((prev) =>
        prev
          ? {
            ...prev,
            linked_algos: prev.linked_algos.filter((a) => a.id !== algoId),
          }
          : null
      );
    } catch (error) {
      console.error("Failed to remove algo:", error);
      setError("Failed to remove algorithm");
    } finally {
      setUpdating(false);
    }
  };


  const handleEditAlgo = (algo: LinkedAlgo) => {
    navigate(`/instances/${id}/linked-algos/${algo.id}/edit`);
  };

  const handleSaveAlgoChanges = async (algoId: number, params: any) => {
    if (!instance) return;

    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      const response = await axios.put(
        `${BACKEND_URL}/api/instances/${instance.id}/linked-algos/${algoId}`,
        {
          name: params.name,
          description: params.description,
          parameters: params.parameters,
          asset_classes: params.asset_classes,
          symbols: params.symbols,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      const updatedAlgo = response.data;
      setInstance((prev) =>
        prev
          ? {
            ...prev,
            linked_algos: prev.linked_algos.map((algo) =>
              algo.id === algoId ? updatedAlgo : algo
            ),
          }
          : null
      );
      setShowAlgoEditModal(false);
      setEditingAlgo(null);
    } catch (error) {
      console.error("Failed to update algo:", error);
      setError("Failed to update algorithm");
    } finally {
      setUpdating(false);
    }
  };

  const handlePause = async () => {
    if (!instance) return;

    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      await axios.put(
        `${BACKEND_URL}/api/trading-instances/${instance.id}`,
        { status: "paused" },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      setInstance((prev) => (prev ? { ...prev, status: "paused" } : null));
    } catch (error) {
      console.error("Failed to pause instance:", error);
      setError("Failed to pause instance");
    } finally {
      setUpdating(false);
    }
  };

  const handleResume = async () => {
    if (!instance) return;

    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      await axios.put(
        `${BACKEND_URL}/api/trading-instances/${instance.id}`,
        { status: "live" },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      setInstance((prev) => (prev ? { ...prev, status: "live" } : null));
    } catch (error) {
      console.error("Failed to resume instance:", error);
      setError("Failed to resume instance");
    } finally {
      setUpdating(false);
    }
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
      );
      if (confirmed) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const saveAllChanges = async () => {
    if (!instance) return;

    try {
      setUpdating(true);
      setError(null);
      const csrfToken = await getCSRFToken();

      // Prepare all current changes
      const updates = {
        name: instanceName,
        broker_account_id: selectedAccount
          ? parseInt(selectedAccount)
          : instance.broker_account_id,
        telegram_enabled: telegramEnabled,
        telegram_bot_token: telegramBotToken,
        telegram_chat_id: telegramChatId,
        telegram_notifications: telegramNotifications,
      };

      const response = await axios.put(
        `${BACKEND_URL}/api/trading-instances/${instance.id}`,
        updates,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      // Update local state with the response data
      setInstance(response.data);
      setHasUnsavedChanges(false);
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save all changes:", error);
      setError("Failed to save changes. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!instance) {
    return <div>Instance not found</div>;
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <h2 className="mb-0">Manage Instance</h2>
          {hasUnsavedChanges && (
            <span className="badge bg-warning text-dark ms-2">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Unsaved Changes
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={saveAllChanges}
            disabled={updating || !hasUnsavedChanges}
          >
            {updating ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Saving...</span>
                </div>
                Saving...
              </>
            ) : (
              "Save All Changes"
            )}
          </Button>
          <Button variant="outline-secondary" onClick={handleBackNavigation}>
            Back
          </Button>
        </div>
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          <i className="fas fa-check-circle me-2"></i>
          All changes saved successfully!
          <button
            type="button"
            className="btn-close"
            onClick={() => setSaveSuccess(false)}
          ></button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Subtle loading indicator for updates */}
      {updating && (
        <div
          className="alert alert-info alert-dismissible fade show"
          role="alert"
        >
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            Updating instance data...
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-8">
          <Form.Group className="mb-4">
            <Form.Label>Instance Name</Form.Label>
            <Form.Control
              type="text"
              value={instanceName}
              onChange={handleNameChange}
              disabled={updating}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Broker Account</Form.Label>
            <Form.Select
              value={selectedAccount}
              onChange={handleAccountChange}
              disabled={updating}
            >
              <option value="">Choose an account...</option>
              {brokers?.map((broker) =>
                broker.accounts?.map((account) => (
                  <option key={account.id} value={account.id}>
                    {getAccountLabel(broker, account)}
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>

          <div className="d-flex gap-4 mb-4">
            <div>
              <h6 className="text-muted mb-1">Balance</h6>
              <h4>${Number(instance.balance || 0).toFixed(2)}</h4>
            </div>
            <div>
              <h6 className="text-muted mb-1">P&L</h6>
              <h4
                style={{
                  color: Number(instance.profit) >= 0 ? "#4caf50" : "#f44336",
                }}
              >
                {Number(instance.profit) >= 0 ? "+" : ""}$
                {Number(instance.profit || 0).toFixed(2)}
              </h4>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col">
              <Form.Group>
                <Form.Label>Algorithms</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Select
                    value={selectedAlgo}
                    onChange={(e) => setSelectedAlgo(e.target.value)}
                    disabled={updating}
                  >
                    <option value="">Select Algorithm</option>
                    {algos &&
                      algos.map((algo) => (
                        <option key={algo.id} value={algo.name}>
                          {algo.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    onClick={() =>
                      handleAddAlgo(
                        algos.find((a) => a.name === selectedAlgo) as Algo
                      )
                    }
                    disabled={!selectedAlgo || updating}
                  >
                    Add
                  </Button>
                </div>
                <div className="border rounded p-3">
                  <div className="d-flex flex-column gap-2">
                    {instance.linked_algos.map((algo) => (
                      <div
                        key={algo.id}
                        className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                      >
                        <div className="flex-grow-1">
                          <div className="fw-bold">{algo.name}</div>
                          <small className="text-muted">ID: {algo.id} | Timeframe: {algo.timeframe}</small>
                        </div>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditAlgo(algo)}
                            disabled={updating}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveAlgo(algo.id)}
                            disabled={updating}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                    {instance.linked_algos.length === 0 && (
                      <div className="text-muted text-center">
                        No algorithms added
                      </div>
                    )}
                  </div>
                </div>
              </Form.Group>
            </div>
          </div>

          {/* Telegram Notifications Configuration */}
          <div className="mb-4">
            <Form.Group>
              <Form.Check
                type="checkbox"
                label="Enable Telegram Notifications"
                checked={telegramEnabled}
                onChange={(e) => {
                  setTelegramEnabled(e.target.checked);
                  setHasUnsavedChanges(true);
                }}
                className="mb-3"
                disabled={updating}
              />
            </Form.Group>

            {telegramEnabled && (
              <div className="border rounded p-3">
                <h6 className="mb-3">Telegram Configuration</h6>

                <Form.Group className="mb-3">
                  <Form.Label>Bot Token</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your Telegram bot token"
                    value={telegramBotToken}
                    onChange={(e) => {
                      setTelegramBotToken(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    disabled={updating}
                  />
                  <Form.Text className="text-muted">
                    Get this from @BotFather on Telegram
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Chat ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your Telegram chat ID"
                    value={telegramChatId}
                    onChange={(e) => {
                      setTelegramChatId(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    disabled={updating}
                  />
                  <Form.Text className="text-muted">
                    Your personal chat ID or group chat ID
                  </Form.Text>
                </Form.Group>

                <div className="mb-3">
                  <Form.Label>Notification Types</Form.Label>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Check
                        type="checkbox"
                        label="Trade Entries"
                        checked={telegramNotifications.send_entries}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            send_entries: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Trade Updates"
                        checked={telegramNotifications.send_updates}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            send_updates: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Trade Exits"
                        checked={telegramNotifications.send_exits}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            send_exits: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                      <Form.Check
                        type="checkbox"
                        label="System Notifications"
                        checked={
                          telegramNotifications.send_system_notifications
                        }
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            send_system_notifications: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                    </div>
                    <div className="col-md-6">
                      <Form.Check
                        type="checkbox"
                        label="Hourly Status"
                        checked={telegramNotifications.send_hourly_status}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            send_hourly_status: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Include Emojis"
                        checked={telegramNotifications.include_emoji}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            include_emoji: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Include Trade Details"
                        checked={telegramNotifications.include_trade_details}
                        onChange={(e) => {
                          setTelegramNotifications({
                            ...telegramNotifications,
                            include_trade_details: e.target.checked,
                          });
                          setHasUnsavedChanges(true);
                        }}
                        disabled={updating}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex gap-3 justify-content-center">
            {instance.status === "live" ? (
              <Button
                variant="warning"
                onClick={handlePause}
                className="px-4"
                disabled={updating}
              >
                Pause Instance
              </Button>
            ) : instance.status === "paused" ? (
              <Button
                variant="success"
                onClick={handleResume}
                className="px-4"
                disabled={updating}
              >
                Resume Instance
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {editingAlgo && instance && (
        <AlgoEditModal
          show={showAlgoEditModal}
          onHide={() => {
            setShowAlgoEditModal(false);
            setEditingAlgo(null);
          }}
          algo={editingAlgo}
          instance={instance}
          onSave={handleSaveAlgoChanges}
        />
      )}
    </div>
  );
};

export default ManageInstance;
